import type { AdminRecord } from '@/types/record'
import type { ColumnDef, PageDefinition, SelectOption } from '@/types/page'
import { formatBoolean, formatCellValue, formatDate, formatDateTime, formatMoney, formatNumber, formatPercent, formatPhone, safeText, statusTone } from '@/utils/format'

export const FIELD_LABELS: Record<string, string> = {
  id: '业务编号',
  name: '名称',
  title: '标题',
  status: '状态',
  status_label: '状态',
  created_at: '创建时间',
  updated_at: '更新时间',
  row_version: '数据版本',
  user_no: '用户编号',
  family_no: '家庭编号',
  plan_no: '计划编号',
  alert_no: '预警编号',
  report_no: '报告编号',
  device_no: '设备编号',
  drug_no: '药品编号',
  message_no: '消息编号',
  order_no: '订单编号',
  ticket_no: '工单编号',
  feedback_no: '反馈编号',
  admin_no: '管理员编号',
  user_name: '用户',
  family_name: '家庭',
  user_id: '用户',
  family_id: '家庭',
  region_id: '所属区域',
  region_name: '所属区域',
  region_path: '区域路径',
  address: '联系地址',
  phone: '联系电话',
  email: '邮箱',
  gender: '性别',
  age: '年龄',
  birth_date: '出生日期',
  tags: '健康标签',
  health_tags: '健康标签',
  risk_level: '风险等级',
  device_bound: '设备绑定',
  last_active_at: '最近活跃',
  registered_at: '注册时间',
  consent_scope: '授权范围',
  emergency_contact: '紧急联系人',
  height_cm: '身高',
  weight_kg: '体重',
  blood_type: '血型',
  medical_history: '既往病史',
  allergy_history: '过敏史',
  family_role: '家庭角色',
  family_type: '家庭类型',
  owner_name: '家庭创建人',
  owner_user_id: '家庭创建人',
  member_count: '成员数',
  guardian_count: '守护人数',
  open_alerts: '未处理预警',
  adherence: '用药依从率',
  device_count: '绑定设备',
  level: '层级',
  level_label: '层级',
  parent_id: '上级区域',
  parent_name: '上级区域',
  path: '区域路径',
  code: '行政编码',
  sort: '排序',
  resource_count: '关联资源',
  resource_summary: '关联明细',
  admin_scope_count: '数据范围数',
  drug_id: '药品',
  drug_name: '药品',
  dosage: '剂量',
  frequency: '服用频次',
  timing: '服用时机',
  start_date: '开始日期',
  end_date: '结束日期',
  owner_id: '负责人',
  pause_reason: '暂停原因',
  next_remind_at: '下次提醒',
  last_reminded_at: '最近提醒',
  remind_count: '累计提醒',
  missed_count: '漏服次数',
  scheduled_at: '计划时间',
  taken_at: '确认时间',
  source: '记录来源',
  recorded_by: '确认人',
  corrected_from: '更正前状态',
  note: '备注',
  metric_type: '指标类型',
  metric_value: '测量值',
  value_display: '测量值',
  unit: '单位',
  reference_range: '参考范围',
  measured_at: '测量时间',
  device_name: '设备',
  is_abnormal: '是否异常',
  severity: '风险等级',
  rule_name: '触发规则',
  alert_type: '预警类型',
  triggered_at: '触发时间',
  sla_due_at: 'SLA 截止',
  closed_at: '关闭时间',
  close_reason: '关闭原因',
  sla_remaining: 'SLA 剩余',
  metric_count: '关联指标',
  history_alerts: '历史预警',
  lower_bound: '下限',
  upper_bound: '上限',
  duration_minutes: '持续时长',
  audience: '适用人群',
  channels: '通知渠道',
  version: '版本',
  report_type: '报告类型',
  period_start: '统计开始',
  period_end: '统计结束',
  template_name: '报告模板',
  generated_at: '生成时间',
  sent_at: '发送时间',
  failure_reason: '失败原因',
  asset_name: '报告文件',
  specialty: '专长',
  family_count: '负责家庭',
  workload: '工作量',
  joined_at: '加入时间',
  bio: '个人简介',
  model_name: '设备型号',
  device_type: '设备类型',
  battery: '电量',
  firmware: '固件版本',
  last_report_at: '最近上报',
  bound_at: '绑定时间',
  fault_note: '故障说明',
  generic_name: '通用名称',
  trade_name: '商品名称',
  approval_no: '批准文号',
  barcode: '条形码',
  category_id: '药品分类',
  category_name: '药品分类',
  otc_type: '处方类型',
  dosage_form: '剂型',
  spec: '规格',
  manufacturer: '生产厂家',
  indication: '适应症',
  usage: '用法用量',
  precautions: '注意事项',
  stock: '当前库存',
  safety_stock: '安全库存',
  review_status: '审核状态',
  price_fen: '价格',
  image_name: '药品图片',
  cover_color: '封面颜色',
  message_type: '消息类型',
  template_id: '通知模板',
  audience_type: '受众范围',
  audience_label: '受众说明',
  content: '内容',
  scheduled_at2: '计划发送',
  delivered: '已送达',
  read_count: '已读',
  failed_count: '失败',
  created_by: '创建人',
  content_template: '内容模板',
  title_template: '标题模板',
  variables: '变量',
  link_name: '跳转目标',
  link_id: '跳转链接',
  category: '分类',
  summary: '摘要',
  cover_name: '封面素材',
  online_at: '上线时间',
  offline_at: '下线时间',
  views: '阅读量',
  question: '问题',
  answer: '回答',
  published_at: '发布时间',
  plan_name: '会员方案',
  period_months: '会员周期',
  benefit_names: '包含权益',
  benefits: '包含权益',
  sales_count: '购买量',
  image_asset_id: '方案封面',
  amount_fen: '实付金额',
  refund_amount_fen: '退款金额',
  refund_reason: '退款原因',
  channel: '支付渠道',
  paid_at: '支付时间',
  target_type: '目标类型',
  target_page: '用户端页面',
  business_action: '业务动作',
  external_url: '外部链接',
  params: '附加参数',
  usage_count: '引用次数',
  asset_type: '素材类型',
  mime_type: '文件类型',
  size_kb: '文件大小',
  width: '宽度',
  height: '高度',
  folder: '所属目录',
  references: '引用位置',
  alt_text: '替代文本',
  uploaded_at: '上传时间',
  file: '选择文件',
  assignee_name: '处理人',
  assignee_id: '处理人',
  priority: '优先级',
  priority_updated_at: '优先级调整时间',
  description: '问题描述',
  resolution: '处理结果',
  conversation: '沟通记录',
  reply: '回复内容',
  last_reply: '最近回复',
  replied_at: '回复时间',
  escalated_at: '升级时间',
  source_page: '来源页面',
  role_codes: '角色',
  role_names: '角色',
  data_scope: '数据范围',
  data_scope_label: '数据范围',
  region_scope: '区域范围',
  mfa_enabled: '双因素认证',
  last_login_at: '最近登录',
  login_history: '登录记录',
  permission_codes: '权限',
  permission_count: '权限数',
  is_builtin: '内置角色',
  account: '登录账号',
  module: '业务模块',
  action: '操作类型',
  resource_type: '资源类型',
  resource_label: '操作对象',
  result: '结果',
  ip: 'IP 地址',
  request_no: '请求编号',
  before_json: '变更前',
  after_json: '变更后',
  detail: '操作说明',
  actor_name: '操作人',
  actor_account: '操作账号',
  total: '总数',
  message: '任务说明',
  task_no: '任务编号',
  task_type: '任务类型',
  progress: '进度',
  plan_id: '关联计划',
  user_agent: '客户端',
  reason: '原因',
  evidence: '凭证',
  relation: '关系',
}

export function fieldLabel(key: string, page?: PageDefinition): string {
  if (page) {
    const column = page.columns.find(item => item.key === key)
    if (column?.label) return column.label
    const form = page.formFields?.find(item => item.key === key)
    if (form?.label) return form.label
    const filter = page.filters.find(item => item.key === key)
    if (filter?.label) return filter.label
    const metric = page.detailMetrics?.find(item => item.label && item.field === key)
    if (metric?.label) return metric.label
  }
  if (FIELD_LABELS[key]) return FIELD_LABELS[key]
  if (/^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/.test(key)) return '业务信息'
  return key
}

export function isSnakeCase(value: string): boolean {
  return /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/.test(value)
}

export function avatarText(value: unknown): string {
  const text = String(value ?? '').trim()
  if (!text) return '康'
  if (/^[A-Za-z]/.test(text)) return text.slice(0, 1).toUpperCase()
  return text.slice(-2)
}

export function avatarTone(value: unknown): string {
  const text = String(value ?? '')
  const tones = ['', ' avatar--blue', ' avatar--pink', ' avatar--amber', ' avatar--purple']
  let count = 0
  for (let i = 0; i < text.length; i += 1) count += text.charCodeAt(i)
  return tones[count % tones.length] ?? ''
}

export function displayCellValue(row: AdminRecord, column: ColumnDef, page: PageDefinition): string {
  const value = row[column.key]
  if (column.type === 'status') {
    return formatCellValue(value, row, {
      columnType: 'status',
      statusMap: page.statusMap,
      options: column.options,
    })
  }
  if (column.type === 'tag' && Array.isArray(value)) return value.join('、')
  return formatCellValue(value, row, {
    columnType: column.type,
    statusMap: page.statusMap,
    options: column.options,
    suffix: column.suffix,
  })
}

export function cellTone(row: AdminRecord, column: ColumnDef, page: PageDefinition): string {
  if (column.type === 'status') {
    const key = String(row[column.key] ?? '')
    return page.statusMap?.[key]?.tone ?? statusTone(String(page.statusMap?.[key]?.label ?? row[column.key] ?? ''))
  }
  if (column.key === 'stock') {
    const stock = Number(row.stock ?? 0)
    const safety = Number(row.safety_stock ?? 0)
    return stock <= 0 ? 'danger' : stock < safety ? 'warning' : 'success'
  }
  if (column.type === 'progress') {
    const value = Number(row[column.key] ?? 0)
    return value < 20 ? 'danger' : value < 50 ? 'warning' : 'success'
  }
  return 'neutral'
}

export function progressValue(row: AdminRecord, column: ColumnDef): number {
  const value = Number(row[column.key] ?? 0)
  return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0
}

export function formatFieldValue(value: unknown, page?: PageDefinition, key = ''): string {
  if (key === 'before_json' || key === 'after_json') {
    return value ? '见下方变更差异' : '—'
  }
  if (Array.isArray(value)) {
    const labels = value.map(item => String(item)).filter(Boolean)
    return labels.length ? labels.join('、') : '—'
  }
  if (typeof value === 'boolean') return formatBoolean(value)
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'number') {
    if (key === 'price_fen' || key === 'amount_fen' || key === 'refund_amount_fen') return formatMoney(value)
    return formatNumber(value)
  }
  const text = String(value)
  if (/^\d{4}-\d{2}-\d{2}T/.test(text)) return formatDateTime(text)
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return formatDate(text)
  if (key === 'phone') return formatPhone(text)
  if (/^\d{11}$/.test(text) && key.includes('phone')) return formatPhone(text)
  if (isSnakeCase(text)) return '—'
  if (page?.statusKey === key) {
    return page.statusMap?.[text]?.label ?? text
  }
  return safeText(text)
}

export function formatCompactNumber(value: unknown): string {
  const number = Number(value)
  if (!Number.isFinite(number)) return '0'
  if (number >= 10000) return `${(number / 10000).toFixed(1)}万`
  return formatNumber(number)
}

export function percent(value: number, precision = 1): string {
  return formatPercent(value, precision)
}

export function safeOptionLabel(options: SelectOption[], value: unknown): string {
  return options.find(item => String(item.value) === String(value ?? ''))?.label ?? String(value ?? '—')
}
